import * as React from "react";
import { Link } from "@tanstack/react-router";
import { type LucideIcon } from "lucide-react";

import { Card, CardContent } from "@/components/ui/card";
import { STATUS_DOT_COLOR, type StatusColor } from "@/lib/status-colors";

// The one KPI tile every Phase-4 dashboard section renders through. `value`
// is a ReactNode (not a plain string) specifically so callers can pass an
// <Amount /> or <StatusBadge /> as the headline figure. `icon`/`tone` are
// optional so existing call sites keep working unstyled; where passed, the
// icon accent reuses the same fixed `StatusColor` vocabulary as
// `StatusBadge`'s dots rather than introducing a second color system.

export interface StatCardProps {
  label: string;
  value: React.ReactNode;
  subLine?: React.ReactNode;
  link?: { to: string; label: string };
  icon?: LucideIcon;
  tone?: StatusColor;
}

export function StatCard({ label, value, subLine, link, icon: Icon, tone = "neutral" }: StatCardProps) {
  const accent = STATUS_DOT_COLOR[tone];

  return (
    <Card className="border-border/70 shadow-none">
      <CardContent className="flex items-start justify-between gap-4 pt-6">
        <div className="min-w-0 space-y-1.5">
          <div className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
            {label}
          </div>
          <div
            className="text-2xl font-semibold tracking-tight text-foreground"
            style={{ fontVariantNumeric: "tabular-nums" }}
          >
            {value}
          </div>
          {subLine && <div className="text-sm text-muted-foreground">{subLine}</div>}
          {link && (
            <div className="pt-0.5">
              <Link to={link.to} className="text-sm text-primary underline-offset-4 hover:underline">
                {link.label}
              </Link>
            </div>
          )}
        </div>
        {Icon && (
          <div
            className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg"
            style={{ backgroundColor: `${accent}1A`, color: accent }}
          >
            <Icon className="h-[18px] w-[18px]" />
          </div>
        )}
      </CardContent>
    </Card>
  );
}
